/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { businessCapacityHeaderControlPanelEntryPoint } from '@jira/spa-apps__business-capacity-header-control-panel/src/businessCapacityHeaderControlPanelEntryPoint.entrypoint.tsx';
import { businessCapacityEntryPoint } from '@jira/spa-apps__business-capacity-people/src/businessCapacityEntryPoint.entrypoint.tsx';
import { businessCapacityWorkEntryPoint } from '@jira/spa-apps__business-capacity-work/src/businessCapacityWorkEntryPoint.entrypoint.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

const ICP_MOST_RECENT_VIEW_COOKIE = 'icp_most_recent_view';

declare global {
	// eslint-disable-next-line vars-on-top
	var __GET_COOKIE__: ((name?: string) => string | undefined) | undefined;
}

type CapacityView = 'people' | 'work';

const isCapacityView = (value: unknown): value is CapacityView =>
	value === 'people' || value === 'work';

const getCookieValue = (name: string) =>
	globalThis?.__GET_COOKIE__?.(name) ??
	globalThis?.document?.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)?.pop();

const getViewFromCookie = (): CapacityView => {
	const mostRecentView = getCookieValue(ICP_MOST_RECENT_VIEW_COOKIE)?.toLowerCase();
	return mostRecentView === 'work' ? 'work' : 'people';
};

type CapacityExtraProps = {
	activeView: CapacityView;
	viewParam: CapacityView | undefined;
	tenantContext: TenantContext;
};

type CapacityEntryPoints = {
	headerControlPanel?: typeof businessCapacityHeaderControlPanelEntryPoint;
	people?: typeof businessCapacityEntryPoint;
	work?: typeof businessCapacityWorkEntryPoint;
};

type BusinessCapacityParentEntryPointComponent = EntryPointComponent<
	{},
	CapacityEntryPoints,
	{},
	CapacityExtraProps
>;

export const businessCapacityParentEntryPoint: EntryPoint<
	BusinessCapacityParentEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<BusinessCapacityParentEntryPointComponent>(
		/* webpackChunkName: "async-jira-business-capacity" */ './BusinessCapacityContainer.tsx',
	),
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		const viewParamRaw = entryPointParams.context.match.params.view;
		const viewParam = isCapacityView(viewParamRaw) ? viewParamRaw : undefined;

		// Use URL param if set, otherwise fall back to cookie.
		const activeView: CapacityView = viewParam ?? getViewFromCookie();

		return {
			extraProps: {
				activeView,
				viewParam,
				tenantContext: entryPointParams.tenantContext,
			},
			entryPoints: {
				headerControlPanel: {
					entryPoint: businessCapacityHeaderControlPanelEntryPoint,
					entryPointParams,
				},
				work:
					activeView === 'work'
						? {
								entryPoint: businessCapacityWorkEntryPoint,
								entryPointParams,
							}
						: undefined,
				people:
					activeView === 'people'
						? {
								entryPoint: businessCapacityEntryPoint,
								entryPointParams,
							}
						: undefined,
			},
		};
	},
});
